import React, { useMemo } from 'react'

import { Card, CardContent, Typography, Box, Grid } from '@material-ui/core'
import {
    People as PeopleIcon,
    Flag as CampaignIcon,
    Mail,
    Phone,
    TrendingUp,
    Person,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
    CampaignProspectStatus,
    DetailedCampaignProspect,
} from '../../../../models/Outreach/types'
import {
    isDetailedCampaignProspect,
    ProspectOrCampaignProspect,
} from '../../../../hooks/Outreach/useLeads'
interface ProspectsStatsProps {
    combinedProspects: ProspectOrCampaignProspect[]
    pagination: {
        count: number
        next: string | null
        previous: string | null
        currentPage: number
        pageSize: number
        totalPages: number
        summary_stats: {
            total_count: number
            in_active_campaign: number
            not_in_campaign: number
            with_email: number
            with_phone: number
            with_profile: number
        } | null
    }
    isLoading?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    statsCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    statsCardLoading: {
        opacity: 0.4,
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        boxShadow: 'none',
        '&:hover': {
            boxShadow: 'none',
        },
    },
    statsCardContent: {
        padding: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    statsIcon: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing(1),
    },
    statsValue: {
        fontSize: '1.875rem',
        fontWeight: 'bold',
        lineHeight: 1.2,
    },
    statsValueLoading: {
        color: theme.palette.text.disabled,
    },
    statsLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        fontWeight: 400,
    },
    statsLabelLoading: {
        color: theme.palette.text.disabled,
    },
    statsIconLoading: {
        opacity: 0.3,
    },
}))

export const ProspectsStats: React.FC<ProspectsStatsProps> = ({
    combinedProspects,
    pagination,
    isLoading = false,
}) => {
    const classes = useStyles()

    // If we have cached summary stats, don't show loading state
    const hasCachedStats = !!pagination.summary_stats
    const shouldShowLoading = isLoading && !hasCachedStats

    const renderStatCard = (
        label: string,
        value: string | number,
        icon: React.ReactElement,
        iconColor: string,
        iconBgColor: string,
    ) => (
        <Card
            className={`${classes.statsCard} ${
                shouldShowLoading ? classes.statsCardLoading : ''
            }`}
        >
            <CardContent className={classes.statsCardContent}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Box>
                        <Typography
                            className={`${classes.statsLabel} ${
                                shouldShowLoading
                                    ? classes.statsLabelLoading
                                    : ''
                            }`}
                        >
                            {label}
                        </Typography>
                        <Typography
                            className={`${classes.statsValue} ${
                                shouldShowLoading
                                    ? classes.statsValueLoading
                                    : ''
                            }`}
                            style={
                                !shouldShowLoading
                                    ? { color: iconColor }
                                    : undefined
                            }
                        >
                            {shouldShowLoading ? '-' : value}
                        </Typography>
                    </Box>
                    <Box
                        className={`${classes.statsIcon} ${
                            shouldShowLoading ? classes.statsIconLoading : ''
                        }`}
                        style={
                            !shouldShowLoading
                                ? {
                                      backgroundColor: iconBgColor,
                                  }
                                : {
                                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                  }
                        }
                    >
                        {React.cloneElement(icon, {
                            style: {
                                fontSize: 16,
                                color: shouldShowLoading
                                    ? '#9ca3af'
                                    : iconColor,
                            },
                        })}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )

    return (
        <Grid container spacing={2} style={{ marginBottom: 8 }}>
            <Grid item xs={6} sm={4} md={2}>
                {renderStatCard(
                    'Total',
                    pagination.summary_stats?.total_count?.toLocaleString() ||
                        0,
                    <PeopleIcon />,
                    '#3b82f6',
                    'rgba(59, 130, 246, 0.1)',
                )}
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                {renderStatCard(
                    'Not in Campaign',
                    pagination.summary_stats?.not_in_campaign?.toLocaleString() ||
                        0,
                    <PeopleIcon />,
                    '#10b981',
                    'rgba(16, 185, 129, 0.1)',
                )}
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                {renderStatCard(
                    'In Campaign',
                    pagination.summary_stats?.in_active_campaign?.toLocaleString() ||
                        0,
                    <CampaignIcon />,
                    '#ef4444',
                    'rgba(239, 68, 68, 0.1)',
                )}
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                {renderStatCard(
                    'With Email',
                    pagination.summary_stats?.with_email?.toLocaleString() || 0,
                    <Mail />,
                    '#10b981',
                    'rgba(16, 185, 129, 0.1)',
                )}
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                {renderStatCard(
                    'With Phone',
                    pagination.summary_stats?.with_phone?.toLocaleString() || 0,
                    <Phone />,
                    '#f59e0b',
                    'rgba(245, 158, 11, 0.1)',
                )}
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                {renderStatCard(
                    'With Profile',
                    pagination.summary_stats?.with_profile?.toLocaleString() ||
                        0,
                    <Person />,
                    '#8b5cf6',
                    'rgba(139, 92, 246, 0.1)',
                )}
            </Grid>
        </Grid>
    )
}
